import * as THREE from 'three';

/**
 * Hướng nhìn chuẩn (camera đứng ở phía nào của mô hình, nhìn vào tâm).
 * Quy ước theo trục thế giới của app: mặc định camera ở +Z nhìn vào gốc
 * (xem SceneManager.resetCamera) nên đó là mặt "trước".
 */
export const STANDARD_VIEWS = {
  front: [0, 0, 1],
  back: [0, 0, -1],
  right: [1, 0, 0],
  left: [-1, 0, 0],
  // Hơi lệch để camera không trùng trục up (OrbitControls)
  top: [0, 1, 0.001],
};

/** Hộp bao thế giới của tất cả module (rỗng -> null). */
export const modulesWorldBox = groups => {
  const box = new THREE.Box3();
  groups.forEach(g => {
    g.updateWorldMatrix(true, true);
    box.expandByObject(g);
  });
  return box.isEmpty() ? null : box;
};

/**
 * Vị trí camera để nhìn vừa khít `box` từ một hướng chuẩn.
 * @returns {{position: number[], target: number[]}}
 */
export const computeStandardView = (name, box, fovDeg = 45, margin = 1.15) => {
  const dir = STANDARD_VIEWS[name];
  if (!dir) return null;
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const radius = Math.max(sphere.radius, 0.3);
  const distance = (radius / Math.sin((fovDeg * Math.PI) / 360)) * margin;
  const d = new THREE.Vector3(...dir).normalize();
  const target = sphere.center;
  const position = target.clone().addScaledVector(d, distance);
  return { position: position.toArray(), target: target.toArray() };
};

/**
 * Đưa camera tới một góc nhìn (position/target), có hoạt ảnh ngắn.
 * `duration = 0` đặt ngay (dùng trong test).
 * @returns {() => void} hàm huỷ hoạt ảnh
 */
export const applyCameraView = (sceneManager, view, duration = 450) => {
  const camera = sceneManager?.camera;
  const controls = sceneManager?.controls;
  if (!camera || !controls || !view) return () => {};
  const toPos = new THREE.Vector3(...view.position);
  const toTarget = new THREE.Vector3(...view.target);
  const set = (pos, target) => {
    camera.position.copy(pos);
    controls.target.copy(target);
    camera.lookAt(target);
    controls.update?.();
  };
  if (!(duration > 0) || typeof requestAnimationFrame !== 'function') {
    set(toPos, toTarget);
    return () => {};
  }
  const fromPos = camera.position.clone();
  const fromTarget = controls.target.clone();
  const start = performance.now();
  let raf = 0;
  const tick = now => {
    const t = Math.min(1, (now - start) / duration);
    const e = t * t * (3 - 2 * t); // smoothstep
    set(fromPos.clone().lerp(toPos, e), fromTarget.clone().lerp(toTarget, e));
    if (t < 1) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
};

/** Góc nhìn hiện tại của camera (để lưu). */
export const captureCameraView = sceneManager => {
  const camera = sceneManager?.camera;
  const controls = sceneManager?.controls;
  if (!camera || !controls) return null;
  return {
    position: camera.position.toArray(),
    target: controls.target.toArray(),
  };
};

/**
 * Bộ DIM đang hiện (lớp, preset, DIM nào bị ẩn) để lưu cùng góc nhìn, nhờ đó
 * mỗi view có thể hiện một tập DIM khác nhau.
 */
export const captureDimState = ({ layers, preset, items }) => ({
  layers: { ...layers },
  preset,
  hiddenIds: (items ?? []).filter(i => i.visible === false).map(i => i.id),
});
